import { v3Url } from '@/utils/fetcher';
import useListLoader from '../base/useListLoader';
import { useOptimisticUpdates } from '../base/useOptimisticUpdates';
import { useRealTimeUpdates } from '../base/useRealTimeUpdates';

function responseToItems(response) {
  const { notifications = {}, companies = {}, projects = {}, teams = {}, users = {} } = response.data.included;

  const projectBudgets = response.data.budgets || [];

  /* eslint-disable no-param-reassign */
  projectBudgets.forEach((budget) => {
    if (budget.isRetainer) {
      budget.budgetCategory = 'retainer';
    } else if (budget.budgetCategory) {
      budget.budgetCategory = budget.budgetCategory.toLowerCase();
    } else {
      budget.budgetCategory = 'standard';
    }
    budget.repeatUnit = budget.repeatUnit.toLowerCase();
    budget.status = budget.status.toLowerCase();
    budget.timelogType = budget.timelogType.toLowerCase();
    budget.type = budget.type.toLowerCase();

    if (projects[budget.projectId]) {
      budget.project = projects[budget.projectId];
    }

    budget.notifications.forEach((_notification, index) => {
      if (notifications[_notification.id]) {
        const notification = notifications[_notification.id];
        notification.notificationMedium = notification.notificationMedium.toLowerCase();
        notification.users = notification.users.map((user) => {
          return users[user.id] ?? user;
        });
        notification.teams = notification.teams.map((_team) => {
          const team = teams[_team.id] ?? _team;
          team.teamId = team.id;
          return team;
        });
        notification.companies = notification.companies.map((_company) => {
          const company = companies[_company.id] ?? _company;
          company.companyId = company.id;
          return company;
        });
        budget.notifications[index] = notification;
      }
    });
    /* eslint-enable no-param-reassign */
  });

  return projectBudgets;
}

/**
 * Loads a list of budgets from the Teamwork v3 API.
 */
export default function useProjectBudgetsLoader({ params, count, pageSize = 50 }) {
  const { state, update, refresh } = useListLoader({
    url: v3Url('projects/budgets'),
    params,
    count,
    responseToItems,
    pageSize,
  });

  useOptimisticUpdates((event) => {
    if (event.type !== 'budget') {
      return;
    }
    update((budgets) => {
      if (event.action === 'create') {
        return budgets.concat(event.budget);
      }
      if (event.action === 'update') {
        return budgets.map((budget) => (budget.id === event.budget.id ? { ...budget, ...event.budget } : budget));
      }
      if (event.action === 'delete') {
        // if it is a repeating budget, remove all the budgets with the same originatorBudgetId
        if (event.budget.isRepeating) {
          return budgets.filter((budget) => {
            return (budget.originatorBudgetId ?? budget.id) !== (event.budget.originatorBudgetId ?? event.budget.id);
          });
        }
        return budgets.filter((budget) => budget.id !== event.budget.id);
      }
      return budgets;
    }, event.promise);
  });

  useRealTimeUpdates((event) => {
    // budget
    if (event.type === 'budget') {
      if (event.originatorBudgetId) {
        refresh();
        return;
      }
      refresh(event.budgetId);
      return;
    }

    // budget expense
    if (event.type === 'budgetExpense') {
      refresh(event.projectBudgetId);
      return;
    }

    // timelog or project rate change
    if (event.type === 'time' || (event.type === 'project' && event.action === 'rate-set')) {
      const budget = state.items.value.find((_budget) => event.projectId === _budget.projectId);
      if (budget) {
        refresh();
      }
      return;
    }

    // installation rate
    if (event.type === 'installation' && event.action === 'rate-set') {
      refresh();
    }
  });

  return state;
}
